import React, { useState, useEffect } from "react";
import dataService from '../../services/dataService';
import { OptionGroup, ProductListItem } from "../../models/productList/types";
import SidebarCreateNewEstimate from "./sidebarCreateNewList";
import ProductListEnumType from "../../../enums/productListEnumType";
import CloseIcon from '@mui/icons-material/Close';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';

interface SidebarAddToListProps {
  name: string | null,
  sku: string | null,
  availability: string | null,
  imgUrl: string,
  mainPictureOverlay: string | null,
  open: boolean;
  onClose: () => void;
  onSave: (id: string, listType: ProductListEnumType) => void;
}

export const SidebarAddToList: React.FC<SidebarAddToListProps> = ({ name, sku, availability, imgUrl, mainPictureOverlay, open, onClose, onSave }) => {
  const [templateGroups, setTemplateGroups] = useState<OptionGroup[]>([]);
  const [selectedValue, setSelectedValue] = useState<string>("");
  const [savedMessage, setSavedMessage] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [sideBarCreateNewListOpen, setSidebarCreateNewListOpen] = useState<boolean>(false);

  useEffect(() => {
    const fetchTemplateGroups = async () => {
      setLoading(true);
      try {
        const result: OptionGroup[] = await dataService.fetchEstimatesAndTemplates();
        setTemplateGroups(result);
        const first = result?.[0]?.options?.[0]?.id;
        if (first) setSelectedValue(first);
      } catch (error) {
        console.error("Failed to load templates:", error);
      } finally {
        setLoading(false);
      }
    };

    if (open) {
      setSidebarCreateNewListOpen(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      fetchTemplateGroups();
    }
  }, [open]);

  const handleSave = () => {
    var selectedItem = templateGroups
      .flatMap(group => group.options)
      .find(option => option.id == selectedValue);
    if (selectedItem) {
      onSave(selectedItem.id, selectedItem.listType);
    }
  };

  const handleSidebarClose = () => {
    setSidebarCreateNewListOpen(false);
  };

  const handelNewListCreation = (productListId: string, listType: ProductListEnumType, newListName: string) => {

    const label = listType === ProductListEnumType.QuoteList ? "Estimates" : "Templates";
    const newOption = {
      id: productListId,
      name: newListName,
      listType
    };

    setSavedMessage(`Saved to ${newOption.name}`);

    setTemplateGroups(prev => {
      const groupIndex = prev.findIndex(g => g.label === label);
      if (groupIndex !== -1) {
        const updated = [...prev];
        updated[groupIndex] = {
          ...updated[groupIndex],
          options: [newOption, ...updated[groupIndex].options]
        };
        return updated;
      } else {
        return [...prev, { label, options: [newOption] }];
      }
    });

    setSelectedValue(productListId);
    setTimeout(() => {
      onSave(productListId, listType);
      setSavedMessage("");
    }, 1000);
  };

  return (
    <div className={`sidebarAddNewListContainer sidebar ${open && !loading ? 'open' : ''}`}>
      <div className="sidebarClose" onClick={onClose}>
        <CloseIcon />
      </div>

      <div className="sidebarAddNewListBody sidebarBody">
        <h3 className="title">Add to Estimate or Template</h3>

        <div className="sidebarProductinfo">
          <div className="sidebarImageHolder">
            <img alt="Product Placeholder" src={imgUrl} style={{ backgroundColor: mainPictureOverlay ?? "" }} />
          </div>
          <div className="sidebarInfoHolder">
            <h3 className="sidebarInfo">{name}</h3>
            <div className="sidebarInfo">{availability}</div>
            <div className="sidebarInfo" style={{ paddingTop: '3px' }}><strong style={{ marginRight: "3px" }}>SKU:</strong>{sku}</div>
          </div>
        </div>

        <div className="sidebarActions">
          <label className="sidebarActionsLabel">Add to existing Estimate or Template</label>

          <Autocomplete<ProductListItem>
            fullWidth
            size="small"
            options={templateGroups.flatMap(group => group.options)}
            groupBy={(option) => {
              const group = templateGroups.find(g => g.options.some(o => o.id === option.id));
              return group?.label ?? "";
            }}
            getOptionLabel={(option) => option.name}
            value={
              templateGroups.flatMap(g => g.options).find(opt => opt.id === selectedValue) || null
            }
            isOptionEqualToValue={(option, value) => option.id === value?.id}
            onChange={(_, newValue) => {
              setSelectedValue(newValue?.id ?? "");
            }}
            componentsProps={{
              popper: {
                sx: {
                  zIndex: 10000,
                },
              },
            }}
            renderGroup={(params) => (
              <li key={params.key}>
                <div style={{ fontWeight: 'bold' ,paddingLeft:16 }}>{params.group}</div>
                <ul style={{ paddingLeft: 16 }}>{params.children}</ul>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                fullWidth
                className="form-input"
                placeholder="Search list..."
                InputLabelProps={{ shrink: false }}
                size="small"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading && <CircularProgress size={20} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />


          <div style={{ margin: "10px 0" }}>
            <div className="orDivider">-OR-</div>
            <strong>
              <span
                role="button"
                tabIndex={0}
                className='linkLike'
                onClick={() => setSidebarCreateNewListOpen(true)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    setSidebarCreateNewListOpen(true);
                  }
                }} >
                Create new Estimate or Template
              </span>
            </strong>


            {savedMessage && (
              <div style={{ marginLeft: "20px", marginTop: "0.5em", color: "#29b05f", fontWeight: "bold" }}>
                {savedMessage}
              </div>
            )}
          </div>
          <SidebarCreateNewEstimate open={sideBarCreateNewListOpen} onClose={handleSidebarClose} createNewList={handelNewListCreation} />
          <div className="buttons" style={{ textAlign: "right" }}>
            <button type="button" className="handCursor blueButtonOutline" onClick={handleSave}>
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

